import { initCartDrawer } from "./cartDrawer/cartDrawer";
import "./cartDrawer/expose-global";
import "./cartDrawer/standardActions";
import { initPopupNotification } from "./popupNotification";
import { initEkMegaMenu, updateMegaMenuActiveStates } from "./ekMegaMenu";
import { initEkMegaMenuMobile, initEkMegaMenuMobileLazy } from "./ekMegaMenuMobile";
import { initEkHeaderScroll } from "./ekHeaderScroll";
import { initBfPercentAnimation } from "./bfPercentAnimation";
import { initProductCardSwatches } from "./initProductCardSwatches";
import { initBfCountdown } from "./bfCountdown";
import { initBfHighlight } from "./bf-highlight";
import { initBfDiscountGridSlider } from "./bfDiscountGridSlider";
import initBfWaveAnimation from "./bfWaveAnimation";
import { initBfHeroSlider } from "./bfHeroSlider";
import { initBf10GridSlider } from "./bf10GridSlider";
import { initBfDealGrid } from "./bfDealGrid";
import { initBeigeweekPromo } from "./beigeweekPromo";
import { initEkHighlightProduct } from "./ekHighlightProduct";
import { initXmasFpHeroFullscreen } from "./xmasFpHeroFullscreen";
import { initEkCategoriesSlider } from "./ekCategoriesSlider";
import { initEkReviewSlides } from "./ekReviewSlides";
import { initEkCountdown } from "./ekCountdown";
import { initVariantOptionLabelPaddingInContext, initVariantOptionLabelPaddingModule } from "./variantOptionLabelPadding";
import { initEkDocumentSearch } from "./ekDocumentSearch";
import { initCollectionFilterGrid } from "./twCollectionFilterGrid";
import { initCompareTable } from "./twCompareTable";
import { initProductBulletsSlider } from "./twProductBulletsSlider";
import { initWoodSelector } from "./twWoodSelector";
import "./twStaticUsps";
import { initCollectionNav } from "./ekCollectionNav";
import { initCollectionFilters, initResultCount } from "./collectionFilters";
import { initMegaMenuThemenweltenSlider } from "./ekMegaMenuThemenweltenSlider";
import { wireCollectionPage } from "./collectionPage/wire";
import "./collectionPage/loadMore";
import "./productRecommendationsSlider";
import "./similarProductsSlider";
import "./recentlyViewedSlider";
import "./singleSlider";
import "./magazinSlider";
import "./trustpilotDrawer";
import "./mediaCardInfo";
import "./ekCommunityPosts";
import "./ekProductFaq";
import "./ekPdpGallery";
import "./twImageReveal";
import { initBerater } from "./ekBerater/index";
import { initWiderrufForm } from "./ekWiderrufForm";
import { initEkBuildYourBundle } from "./ekBuildYourBundle";
import "./ekUpsellDrawer";
import { initKlaviyoFormTracking } from "./ekKlaviyoFormTracking";

// Initialize cart drawer with default settings or via window config if available
// This expects a global configuration object to be present if settings are needed
declare global {
  interface Window {
    themeSettings?: {
      isBf: boolean;
    };
    navBarHeight?: number;
    initVariantOptionLabelPaddingInContext?: (root: ParentNode) => void;
  }
}

const isBf = window.themeSettings?.isBf || false;
initCartDrawer({ isBf });

const runOnReady = (callback: () => void) => {
  if (document.readyState === "complete") {
    callback();
  } else {
    document.addEventListener("DOMContentLoaded", callback);
  }
};

const runOnSectionLoad = (callback: (target: ParentNode) => void) => {
  document.addEventListener("shopify:section:load", (e) => {
    const target = (e.target as ParentNode) || document;
    callback(target);
  });
};

// Initialize beigeweek promo handler (runs immediately to catch URL params)
initBeigeweekPromo();

// Window-level listener, registered immediately so early Klaviyo popup events aren't missed
initKlaviyoFormTracking();

runOnReady(() => initPopupNotification());

runOnReady(() => {
  initEkMegaMenu();
  updateMegaMenuActiveStates();
  // Re-position indicator now that data-current is set
  window.dispatchEvent(new Event("resize"));
});
runOnSectionLoad((target) => {
  initEkMegaMenu(target);
  updateMegaMenuActiveStates();
  window.dispatchEvent(new Event("resize"));
});

runOnReady(() => {
  initEkMegaMenuMobile();
  initEkMegaMenuMobileLazy();
});
runOnSectionLoad((target) => initEkMegaMenuMobile(target));

runOnReady(() => initEkHeaderScroll());
runOnSectionLoad((target) => initEkHeaderScroll(target));

runOnReady(() => initBfPercentAnimation());
runOnSectionLoad((target) => initBfPercentAnimation(target));

// Initialize swatch cards globally and on section load
runOnReady(() => initProductCardSwatches());
runOnSectionLoad((target) => initProductCardSwatches(target));

// Initialize BF countdown globally and on section load
runOnReady(() => initBfCountdown());
runOnSectionLoad((target) => initBfCountdown(target));

// Initialize BF highlight custom swatches (separate from generic product card swatches)
runOnReady(() => initBfHighlight());
runOnSectionLoad((target) => initBfHighlight(target));

// Initialize EK highlight product slider
runOnReady(() => initEkHighlightProduct());
runOnSectionLoad((target) => initEkHighlightProduct(target));

// Initialize BF discount grid slider
runOnReady(() => initBfDiscountGridSlider());
runOnSectionLoad((target) => initBfDiscountGridSlider(target));

// Initialize wave animation once page has fully loaded (including all resources)
if (document.readyState === "complete") {
  // Page already fully loaded, initialize immediately
  initBfWaveAnimation();
} else {
  // Wait for window load event (fires when all resources are loaded)
  window.addEventListener("load", () => initBfWaveAnimation());
}
runOnSectionLoad((target) => initBfWaveAnimation(target));

// Initialize BF Hero Slider
runOnReady(() => initBfHeroSlider());
runOnSectionLoad((target) => initBfHeroSlider(target));

// Initialize BF 10 Grid Slider
runOnReady(() => initBf10GridSlider());
runOnSectionLoad((target) => initBf10GridSlider(target));

runOnReady(() => initBfDealGrid());
runOnSectionLoad((target) => initBfDealGrid(target));

// Initialize XMAS FP Hero Fullscreen
runOnReady(() => initXmasFpHeroFullscreen());
runOnSectionLoad((target) => initXmasFpHeroFullscreen(target));

// Initialize EK Categories Slider
runOnReady(() => initEkCategoriesSlider());
runOnSectionLoad((target) => initEkCategoriesSlider(target));

// Initialize EK Review Slides
runOnReady(() => initEkReviewSlides());
runOnSectionLoad((target) => initEkReviewSlides(target));

// Initialize EK countdown
runOnReady(() => initEkCountdown());
runOnSectionLoad((target) => initEkCountdown(target));

// Variant option label conditional padding (one line vs two lines)
window.initVariantOptionLabelPaddingInContext = initVariantOptionLabelPaddingInContext;
runOnReady(() => initVariantOptionLabelPaddingModule());
runOnSectionLoad((target) => initVariantOptionLabelPaddingInContext(target));
// Initialize Document Search
runOnReady(() => initEkDocumentSearch());
runOnSectionLoad((target) => initEkDocumentSearch(target));

// Initialize TW Collection Filter Grid
runOnReady(() => initCollectionFilterGrid());
runOnSectionLoad((target) => initCollectionFilterGrid(target));

// Initialize TW Compare Table
runOnReady(() => initCompareTable());
runOnSectionLoad((target) => initCompareTable(target));

runOnReady(() => initProductBulletsSlider());
runOnSectionLoad((target) => initProductBulletsSlider(target));

// Initialize TW Wood Selector
runOnReady(() => initWoodSelector());
runOnSectionLoad((target) => initWoodSelector(target));

// Collection page: router, subcollection nav, filters
wireCollectionPage();
runOnReady(() => {
  initCollectionNav();
  initCollectionFilters();
  initResultCount();
  initMegaMenuThemenweltenSlider();
});
runOnSectionLoad((target) => {
  initCollectionNav(target);
  initCollectionFilters(target);
  initMegaMenuThemenweltenSlider(target);
});

runOnReady(() => initBerater());
runOnSectionLoad((target) => initBerater(target));

runOnReady(() => initWiderrufForm());
runOnSectionLoad((target) => initWiderrufForm(target));

runOnReady(() => initEkBuildYourBundle());
runOnSectionLoad((target) => initEkBuildYourBundle(target));
